import type { HTMLAttributes, ReactNode } from "react"
import { cn } from "@/lib/cn"

// Pill / chip element used for status, category, count badges.
// Tone maps to brand intent — never specify hex in callers.
//
// The five canonical tones per the design prototype are
// `success / warning / danger / info / gold`. `error` is kept as an alias
// for `danger` so the ~30 existing callsites don't need to migrate; new
// code should reach for `danger`. `red` and `neutral` are pre-existing
// non-tone variants — `red` is brand-red specifically (different from
// danger which is the error/destructive tone).
export type BadgeTone =
  | "gold"
  | "red"
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "error" // alias for danger; kept for backwards compat

const TONE: Record<BadgeTone, string> = {
  gold:    "border-vip-gold/30 bg-vip-goldMute text-vip-goldSoft",
  red:     "border-brand-red/30 bg-brand-redSoft text-brand-red",
  neutral: "border-border bg-white/5 text-app-text",
  success: "border-tone-success/40 bg-tone-successSoft text-tone-success",
  warning: "border-tone-warning/40 bg-tone-warningSoft text-tone-warning",
  danger:  "border-tone-danger/40 bg-tone-dangerSoft text-tone-danger",
  info:    "border-tone-info/40 bg-tone-infoSoft text-tone-info",
  error:   "border-tone-danger/40 bg-tone-dangerSoft text-tone-danger",
}

type Props = HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone
  children: ReactNode
}

export function Badge({ tone = "neutral", className, children, ...rest }: Props) {
  return (
    <span
      {...rest}
      className={cn(
        "inline-flex items-center rounded-chip border px-3 py-1 text-xs font-medium",
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}
